import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { CgArrowsExchange } from "react-icons/cg";
import { AsyncPaginate } from "react-select-async-paginate";
import { startCase } from "lodash";
import PropTypes from "prop-types";

import {
  getAllPlaces,
  getAllRouteNumCategories,
  getSearchedRoutes,
  getSpecificRoute,
} from "api/vehicleRouteApi";
import LoadingIndicator from "common/LoadingIndicator";
import RoutesTable from "common/RoutesTable";
import SearchWelcomeComponent from "common/SearchWelcomeComponent";
import { PAGE_FORMATTING_BASE } from "constants/common";

const GeneratedSelectBoxPlaces = ({
  control,
  paramName,
  loadViaOptions,
  onChange,
}) => (
  <Controller
    control={control}
    name={paramName}
    render={({ field }) => (
      <AsyncPaginate
        isSearchable
        isClearable
        id={`${paramName}_select`}
        menuPlacement="auto"
        className="w-full sm:w-6/12"
        value={field.value}
        loadOptions={loadViaOptions}
        onChange={(value) => {
          onChange(field, value, paramName);
        }}
        noOptionsMessage={() => "Not available"}
        debounceTimeout={500}
        placeholder={`Select ${startCase(paramName)}`}
        getOptionValue={(option) => option.value}
        getOptionLabel={(option) => option.label}
        additional={{ page: 1 }}
      />
    )}
  />
);

const GeneratedSelectBoxRoutes = ({
  control,
  paramName,
  loadRouteCategorizedOptions,
  onChange,
  routeCategory,
  routeNum,
}) => (
  <Controller
    control={control}
    name={paramName}
    render={({ field }) => (
      <AsyncPaginate
        isSearchable
        isClearable
        id={`${paramName}_select`}
        menuPlacement="auto"
        className="w-full"
        defaultValue={() => {
          if (routeCategory && routeNum) {
            return {
              route_category: routeCategory,
              route_num_name: routeNum,
            };
          } else {
            return null;
          }
        }}
        loadOptions={loadRouteCategorizedOptions}
        onChange={(value) => {
          onChange(field, value, paramName);
        }}
        noOptionsMessage={() => "Not available"}
        debounceTimeout={500}
        placeholder={`Select ${startCase(paramName)}`}
        getOptionValue={(option) => option}
        getOptionLabel={(option) => (
          <div className="flex flex-col py-1">
            <span className="font-semibold"> {option.route_num_name} </span>
            <span className="text-sm">{option.route_category}</span>
          </div>
        )}
        additional={{ page: 1 }}
      />
    )}
  />
);

const VehicleRoutesSearch = () => {
  document.title = "Search for Routes - Banglar Transit";

  const { control, watch, setValue, reset, register } = useForm();

  // Custom hook to get search params from URL
  const getSearchParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      get: (key) => params.get(key),
      delete: (key) => params.delete(key),
      set: (key, value) => params.set(key, value),
      toString: () => params.toString(),
      entries: () => params.entries(),
    };
  };

  const [searchParams, setSearchParamsState] = useState(getSearchParams());
  const route_category = searchParams.get("route_category");
  const route_num_name = searchParams.get("route_num_name");
  const destination_1 = searchParams.get("destination_1");
  const destination_2 = searchParams.get("destination_2");
  const searchType = searchParams.get("search_type");
  const [isLoading, setIsLoading] = useState(false);
  const [resultRoutesList, setResultRoutesList] = useState([]);

  const setSearchParams = (updater) => {
    const newParams =
      typeof updater === "function"
        ? updater(new URLSearchParams(window.location.search))
        : new URLSearchParams(updater);

    const newUrl = `${window.location.pathname}?${newParams.toString()}`;
    window.history.pushState({}, "", newUrl);
    setSearchParamsState(getSearchParams());
  };

  const onSubmit = (data) => {
    const currentParams = new URLSearchParams(window.location.search);
    const updatedParams = {
      ...Object.fromEntries(currentParams.entries()),
      ...data,
    };
    setSearchParams(updatedParams);

    window.scrollTo(0, 0);
  };

  const onChange = (field, value, paramName) => {
    field.onChange(value);
    localStorage.clear();

    if (paramName === "route_num_category") {
      if (value) {
        onSubmit({
          route_category: value.route_category,
          route_num_name: value.route_num_name,
        });
      } else {
        const currentParams = new URLSearchParams(window.location.search);
        currentParams.delete("route_category");
        currentParams.delete("route_num_name");
        setSearchParams(currentParams);
      }
    } else {
      if (value) {
        onSubmit({ [paramName]: value.value });
      } else {
        const currentParams = new URLSearchParams(window.location.search);
        currentParams.delete(paramName);
        setSearchParams(currentParams);
      }

      if (paramName === "destination_1" && value === null) {
        setValue("destination_2", null);
        const currentParams = new URLSearchParams(window.location.search);
        currentParams.delete("destination_2");
        setSearchParams(currentParams);
      }
    }
  };

  const loadViaOptions = async (search, _, { page }) => {
    const {
      data: { data, meta },
    } = await getAllPlaces(page, search);

    return {
      options: JSON.parse(data),
      hasMore: meta?.has_more,
      additional: {
        page: page + 1,
      },
    };
  };

  const loadRouteCategorizedOptions = async (search, _, { page }) => {
    const {
      data: { data, meta },
    } = await getAllRouteNumCategories(page, search);

    return {
      options: JSON.parse(data),
      hasMore: meta?.has_more,
      additional: {
        page: page + 1,
      },
    };
  };

  useEffect(() => {
    if (!searchType) {
      const currentParams = new URLSearchParams(window.location.search);
      currentParams.set("search_type", "routes");
      setSearchParams(currentParams);
    }
  }, [searchType, setSearchParams]);

  useEffect(() => {
    setIsLoading(true);
    let resettingData = {};

    if (route_category && route_num_name) {
      resettingData.label = "";
      resettingData.value = {
        route_category,
        route_num_name,
      };
    } else {
      resettingData = { value: null };
    }

    if (destination_1) {
      resettingData.destination_1 = {
        label: destination_1,
        value: destination_1,
      };
    } else {
      resettingData.destination_1 = null;
    }

    if (destination_2) {
      resettingData.destination_2 = {
        label: destination_2,
        value: destination_2,
      };
    } else {
      resettingData.destination_2 = null;
    }

    reset(resettingData);

    if (destination_1) {
      getSearchedRoutes(destination_1, destination_2).then((response) => {
        setResultRoutesList(response.data);
        setIsLoading(false);
      });
    } else if (route_category) {
      getSpecificRoute(route_category, route_num_name).then((response) => {
        setResultRoutesList([response.data]);
        setIsLoading(false);
      });
    } else {
      setResultRoutesList([]);
      setIsLoading(false);
    }
  }, [destination_1, destination_2, route_category, route_num_name, reset]);

  return (
    <main className={PAGE_FORMATTING_BASE}>
      <h1 className="text-3xl my-6 font-semibold">The search for routes</h1>

      <form>
        <div className="flex flex-col sm:flex-row gap-6 xl:gap-8 my-8">
          Search By:
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-8">
            <div className="flex items-center">
              <input
                {...register("search_type")}
                className="focus:ring-transparent h-5 w-5 text-primary-500 border-neutral-300 !checked:bg-primary-500 bg-transparent mr-2"
                type="radio"
                value="routes"
                id="routes"
                name="parkingType"
                onClick={() => {
                  setSearchParams({ search_type: "routes" });
                }}
                checked={searchType === "routes"}
              />
              <label htmlFor="routes"> Routes </label>
            </div>
            <div className="flex items-center">
              <input
                {...register("search_type")}
                className="focus:ring-transparent h-5 w-5 text-primary-500 border-neutral-300 !checked:bg-primary-500 bg-transparent mr-2"
                type="radio"
                value="places"
                id="places"
                name="parkingType"
                onClick={() => {
                  setSearchParams({ search_type: "places" });
                }}
                checked={searchType === "places"}
              />
              <label htmlFor="places"> Places </label>
            </div>
          </div>
        </div>

        {searchType === "routes" && (
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-6 xl:gap-8">
            <GeneratedSelectBoxRoutes
              control={control}
              paramName="route_num_category"
              loadRouteCategorizedOptions={loadRouteCategorizedOptions}
              onChange={onChange}
              searchParams={searchParams}
              setSearchParams={setSearchParams}
              routeCategory={route_category}
              routeNum={route_num_name}
            />
          </div>
        )}

        {searchType === "places" && (
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-6 xl:gap-8">
            <GeneratedSelectBoxPlaces
              control={control}
              paramName="destination_1"
              loadViaOptions={loadViaOptions}
              onChange={onChange}
              searchParams={searchParams}
              setSearchParams={setSearchParams}
            />

            {watch("destination_1") && watch("destination_2") && (
              <button
                type="button"
                onClick={() => {
                  setSearchParams((searchParams) => {
                    const old_destination_1 = searchParams.get("destination_1");
                    const old_destination_2 = searchParams.get("destination_2");

                    searchParams.set("destination_1", old_destination_2);
                    searchParams.set("destination_2", old_destination_1);
                    return searchParams;
                  });
                }}
                className="self-center border border-1 text-rose-700 p-1 rounded-full bg-orange-100 shadow-lg"
                title="Swap destinations"
              >
                <CgArrowsExchange size={40} className="rotate-90 md:rotate-0" />
              </button>
            )}

            {watch("destination_1") && (
              <GeneratedSelectBoxPlaces
                control={control}
                paramName="destination_2"
                loadViaOptions={loadViaOptions}
                onChange={onChange}
                searchParams={searchParams}
                setSearchParams={setSearchParams}
              />
            )}
          </div>
        )}
      </form>

      <div className="mt-5 w-full">
        {isLoading ? (
          <LoadingIndicator />
        ) : resultRoutesList.length === 0 ? (
          destination_2 ? (
            <SearchWelcomeComponent
              text="No direct buses. Searching feature for break journeys will come
                soon."
              negative
            />
          ) : (
            <SearchWelcomeComponent
              text="Get started with the search feature by selecting a places from
                the select box."
            />
          )
        ) : (
          <RoutesTable
            isLoading={isLoading}
            relevantBusRoutes={resultRoutesList}
            showingSearchResult
          />
        )}
      </div>
    </main>
  );
};

GeneratedSelectBoxPlaces.propTypes = {
  control: PropTypes.object,
  paramName: PropTypes.string,
  loadViaOptions: PropTypes.func,
  onChange: PropTypes.func,
};

GeneratedSelectBoxRoutes.propTypes = {
  control: PropTypes.object,
  paramName: PropTypes.string,
  loadRouteCategorizedOptions: PropTypes.func,
  onChange: PropTypes.func,
  routeNum: PropTypes.string,
  routeCategory: PropTypes.string,
};

export default VehicleRoutesSearch;
